import { sectionNames } from "@saleor/intl";
import { asSortParams } from "@saleor/utils/sort";
import {
  getArrayQueryParamFromParsed,
  parseQueryString
} from "@saleor/utils/urls";
import { parse as parseQs } from "qs";
import React from "react";
import { useIntl } from "react-intl";
import { Route, Routes, useLocation, useParams } from "react-router-dom";

import { WindowTitle } from "../components/WindowTitle";
import {
  KAMSQueryParams,
  ProductImageUrlQueryParams,
  ProductListUrlQueryParams,
  ProductListUrlSortField,
  ProductMainCreateUrlQueryParams,
  ProductUrlQueryParams,
  ProductVariantEditUrlQueryParams
} from "./urls";
import ProductCreate from "./views/ProductCreate";
import ProductImageComponent from "./views/ProductImage";
import ProductListComponent from "./views/ProductList";
import ProductUpdateComponent from "./views/ProductUpdate";
import ProductVariantComponent from "./views/ProductVariant";
import ProductVariantCreateComponent from "./views/ProductVariantCreate";
import ProductVariantCreatorComponent from "./views/ProductVariantCreator";

const ProductList: React.FC = () => {
  const location = useLocation();
  const qs = parseQs(location.search.substr(1));
  const productFindabilityArr = getArrayQueryParamFromParsed(
    qs.productFindability
  );
  const isUnlistedDraft = productFindabilityArr.includes("UNLISTED");
  if (isUnlistedDraft) {
    productFindabilityArr.push("DRAFT");
  }
  const categoriesArr = getArrayQueryParamFromParsed(qs.categories);
  const params = asSortParams(
    {
      ...qs,
      ...(productFindabilityArr.length > 0 && {
        productFindability: productFindabilityArr
      }),
      ...(categoriesArr.length > 0 && { categories: categoriesArr })
    },
    ProductListUrlSortField
  ) as ProductListUrlQueryParams;

  return <ProductListComponent params={params} />;
};

const ProductUpdate: React.FC = () => {
  const location = useLocation();
  const { id } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params = parseQueryString<ProductUrlQueryParams>(
    location.search.substr(1)
  );

  return (
    <ProductUpdateComponent
      id={decodeURIComponent(id)}
      params={{
        ...params,
        ids: getArrayQueryParamFromParsed(qs.ids)
      }}
    />
  );
};

const ProductVariant: React.FC = () => {
  const location = useLocation();
  const { variantId, productId } = useParams();
  const params = parseQueryString<ProductVariantEditUrlQueryParams>(
    location.search.substr(1)
  );

  return (
    <ProductVariantComponent
      variantId={decodeURIComponent(variantId)}
      productId={decodeURIComponent(productId)}
      params={params}
    />
  );
};

const ProductImage: React.FC = () => {
  const location = useLocation();
  const { imageId, productId } = useParams();
  const params = parseQueryString<ProductImageUrlQueryParams>(
    location.search.substr(1)
  );

  return (
    <ProductImageComponent
      imageId={decodeURIComponent(imageId)}
      productId={decodeURIComponent(productId)}
      params={params}
    />
  );
};

const ProductVariantCreate: React.FC = () => {
  const location = useLocation();
  const { id } = useParams();
  const params = parseQueryString<KAMSQueryParams>(location.search, {
    ignoreQueryPrefix: true
  });

  return (
    <ProductVariantCreateComponent
      productId={decodeURIComponent(id)}
      params={params}
    />
  );
};

const ProductVariantCreator: React.FC = () => {
  const { id } = useParams();
  return <ProductVariantCreatorComponent id={decodeURIComponent(id)} />;
};

const ProductCreateComponent: React.FC = () => {
  const location = useLocation();
  const params = parseQueryString<ProductMainCreateUrlQueryParams>(
    location.search,
    { ignoreQueryPrefix: true }
  );
  return <ProductCreate params={params} />;
};

const Component = () => {
  const intl = useIntl();
  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.products)} />
      <Routes>
        <Route path={""} element={<ProductList />} />
        <Route path={"add"} element={<ProductCreateComponent />} />
        <Route
          path={":id/variant-creator"}
          element={<ProductVariantCreator />}
        />
        <Route path={":id/variant/add"} element={<ProductVariantCreate />} />
        <Route
          path={":productId/variant/:variantId"}
          element={<ProductVariant />}
        />
        <Route path={":productId/image/:imageId"} element={<ProductImage />} />
        <Route path={":id"} element={<ProductUpdate />} />
      </Routes>
    </>
  );
};

export default Component;
